import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils.js";

/** 32px square icon control shared by the Inbox list, thread, and Details headers. */
export const InboxIconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
>(function InboxIconButton({ className, active = false, type = "button", ...props }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        "relative grid size-8 shrink-0 place-items-center rounded-[9px] text-inbox-muted transition-[background-color,color] duration-150",
        "hover:bg-inbox-hover hover:text-inbox-strong aria-expanded:bg-inbox-hover aria-expanded:text-inbox-strong",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
        "disabled:pointer-events-none disabled:opacity-50",
        active && "text-primary hover:text-primary aria-expanded:text-primary",
        className,
      )}
      {...props}
    />
  );
});

/** Small count badge pinned to the inline-end corner of an InboxIconButton. */
export function InboxIconCount({ count }: { count: number }): React.JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className="absolute top-[3px] end-[2px] grid h-[15px] min-w-[15px] place-items-center rounded-full bg-primary px-[3px] text-[0.59rem] leading-none font-extrabold text-white tabular-nums shadow-[0_0_0_2px_#fff]"
      aria-hidden="true"
    >
      {count}
    </span>
  );
}

/** 28px list-tool toggle. Pressed (`aria-pressed`) or `active` controls use the mint state. */
export const InboxQuickButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
>(function InboxQuickButton({ className, active = false, type = "button", ...props }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        "relative inline-grid h-7 min-w-[30px] shrink-0 place-items-center rounded-lg border border-transparent px-1.5 text-inbox-muted transition-[background-color,color,border-color] duration-150",
        "hover:bg-inbox-hover hover:text-inbox-strong aria-expanded:bg-inbox-hover aria-expanded:text-inbox-strong",
        "aria-pressed:border-[#c8e7da] aria-pressed:bg-brand-mint aria-pressed:text-primary",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
        active && "border-[#c8e7da] bg-brand-mint text-primary hover:bg-brand-mint hover:text-primary",
        className,
      )}
      {...props}
    />
  );
});

/** Count badge for an InboxQuickButton. */
export function InboxQuickCount({ count }: { count: number }): React.JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className="absolute -top-1 -end-1 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-primary px-[3px] text-[0.59rem] leading-none font-bold text-white tabular-nums shadow-[0_0_0_2px_#fff]"
      aria-hidden="true"
    >
      {count}
    </span>
  );
}
